import cx from 'classnames'
import { useRef, useState } from 'react'
import { Pressable, useHover } from '@react-aria/interactions'
import { useDropzone } from 'react-dropzone'
import dayjs from 'dayjs'

import Styles from './ScreenshotFallback.module.scss'

import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useFileUpload } from '../BrandProfileWebsite/UploadModal/useFileUpload'
import { useCreateBrandProfile } from 'hooks/queries/brandProfiles/useCreateBrandProfile'

import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { ArrowLeft, ArrowRight, ImageAdd, LinkExternal, Trash2, Upload5 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { BrandProfile, MediaObject } from 'types'
import { Dropzone } from './Dropzone'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useUpdateIterableProfile } from 'hooks/queries/useUpdateIterableProfile'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { Heading } from 'blaze-ui/Heading/Heading'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'

interface Props {
  onBack: () => void
  onNext: (brandProfile: BrandProfile) => void
  url?: string
  brandKitId?: number
  selectedLanguage?: string
}

export const ScreenshotFallback = ({ onBack, onNext, brandKitId, selectedLanguage, url }: Props) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { isMobile } = useResponsiveDesign()
  const { mutateAsync: updateIterableProfile } = useUpdateIterableProfile()
  const { mutate, isLoading: isLoadingBrandProfile } = useCreateBrandProfile()
  const [screenshots, setScreenShots] = useState<MediaObject[]>([])
  const { handleUpload, isLoading } = useFileUpload()
  const { hoverProps, isHovered } = useHover({})
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (files) => {
      const dismiss = showToastMessage('Uploading...', 'loading', undefined, 0)
      const newScreenshots = await handleUpload(files)
      if (newScreenshots) setScreenShots([...screenshots, ...newScreenshots])
      dismiss?.()
    },
    accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  })
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = () => {
    if (screenshots.length === 0) {
      showToastMessage('You must upload a screenshot')
      return
    }

    mutate(
      {
        url: screenshots[0].source_url,
        screenshot_media_object_ids: screenshots?.map((screenshot) => screenshot.id) || [],
        brandKitId,
        language: selectedLanguage,
        timezone: dayjs.tz.guess(),
      },
      {
        onSuccess: (data) => {
          updateIterableProfile({ onboarding_device: isMobile ? 'mobile' : 'desktop' })
          onNext(data.brand_profile)
        },
        onError: (error) => {
          showErrorMessageToast(error.message)
        },
      },
    )
  }

  const absoluteUrl = url?.startsWith('http') ? url : `https://${url}`

  if (isOnboardingRedesign) {
    return (
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                  Back
                </Button>
              </ResponsiveDesign.Desktop>
              <Button
                size="lg"
                onPress={handleSubmit}
                aria-label="Continue"
                isDisabled={isLoadingBrandProfile || isLoading}
                fullWidth={isMobile}
              >
                Continue
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <MultiStepStepLayout
          scrollContainerClassName={Styles.scrollContainer}
          title="We couldn't access your site"
          description="Upload 2-3 screenshots of your site to create your Brand Profile and Brand Kit. We will identify your business and visual identity."
        >
          <div className={Styles.container}>
            {url && (
              <div className={Styles.header}>
                <div className={Styles.websiteLinkButton}>
                  <ButtonLink
                    frontIcon={LinkExternal}
                    variant="tertiary"
                    target="_blank"
                    size="lg"
                    to={absoluteUrl}
                    aria-label="Back"
                  >
                    {url}
                  </ButtonLink>
                </div>
              </div>
            )}
            <div className={Styles.instructions}>
              <Heading level={4}>
                Upload screenshots from your website <span className={Styles.redAsterisk}>*</span>
              </Heading>
              <Text variant="secondary" color="var(--dark-40)">
                The more screenshots you provide that represent your brand visually, the sharper your Brand Profile and
                Brand Kit
              </Text>
              <ul className={Styles.list}>
                <li>
                  <Text variant="secondary" color="var(--dark-80)">
                    Take a screenshot of your homepage
                  </Text>
                </li>
                <li>
                  <Text variant="secondary" color="var(--dark-80)">
                    Screenshot of your main services or products
                  </Text>
                </li>
                <li>
                  <Text variant="secondary" color="var(--dark-80)">
                    Any other pages (optional)
                  </Text>
                </li>
              </ul>
            </div>
            <div className={cx(Styles.documentsContainer, { [Styles.withImages]: screenshots.length > 0 })}>
              {screenshots.length === 0 ? (
                <Dropzone
                  onUpload={(mediaObjects: MediaObject[]) => setScreenShots([...screenshots, ...mediaObjects])}
                />
              ) : null}
              {screenshots.length > 0 ? (
                <div className={Styles.screenshotsContainer}>
                  <div className={Styles.previewCarousel} {...getRootProps()}>
                    {screenshots.map((screenshot, index) => {
                      return (
                        <div key={screenshot.id} className={Styles.screenshotPreview}>
                          <img src={screenshot.source_url} alt={`Screenshot ${index}`} />
                          <div className={Styles.removeScreenshotButtonContainer}>
                            <IconButton
                              variant="secondary"
                              size="xs"
                              icon={Trash2}
                              onPress={() => setScreenShots(screenshots.filter(({ id }) => id !== screenshot.id))}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className={Styles.addScreenshotButtonContainer}>
                    <input {...getInputProps()} ref={inputRef} aria-label="Upload files" />
                    <Button
                      variant="secondary"
                      size="xl"
                      frontIcon={Upload5}
                      onPress={() => inputRef.current?.click()}
                      isDisabled={isLoading}
                    >
                      Upload More Screenshots
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </MultiStepStepLayout>
      </StickyFooterProvider>
    )
  }

  return (
    <MultiStepStepLayout
      title="We couldn't access your site"
      description="Upload 2-3 screenshots of your site to create your Brand Profile and Brand Kit. We will identify your business and visual identity."
    >
      <div className={Styles.container}>
        {url && (
          <div className={Styles.header}>
            <div className={Styles.websiteContainer}>
              <Text variant="largeList">{url}</Text>
              <a href={absoluteUrl} target="_blank" rel="noopener noreferrer" className={Styles.linkExternal}>
                <LinkExternal color="var(--dark-80)" />
              </a>
            </div>
          </div>
        )}
        <div className={cx(Styles.documentsContainer, { [Styles.withImages]: screenshots.length > 0 })}>
          {screenshots.length === 0 ? (
            <Dropzone onUpload={(mediaObjects: MediaObject[]) => setScreenShots([...screenshots, ...mediaObjects])} />
          ) : null}
          {screenshots.length > 0 ? (
            <div className={Styles.previewCarousel} {...getRootProps()}>
              {screenshots.map((screenshot, index) => {
                return (
                  <div key={screenshot.id} className={Styles.screenshotPreview}>
                    <img src={screenshot.source_url} alt={`Screenshot ${index}`} />
                    <div className={Styles.removeScreenshotButtonContainer}>
                      <IconButton
                        variant="secondary"
                        size="xs"
                        icon={Trash2}
                        onPress={() => setScreenShots(screenshots.filter(({ id }) => id !== screenshot.id))}
                      />
                    </div>
                  </div>
                )
              })}
              <div className={Styles.addScreenshotButtonContainer} {...hoverProps}>
                <input {...getInputProps()} ref={inputRef} aria-label="Upload files" />
                <Pressable onPress={() => inputRef.current?.click()} isDisabled={isLoading}>
                  <div
                    className={cx(Styles.addScreenshotButtonOverlay, {
                      [Styles.disabled]: isLoading,
                      [Styles.hovered]: isHovered,
                    })}
                  />
                </Pressable>
                <div className={Styles.addScreenshotButton}>
                  <ImageAdd size={24} color="var(--selected-fill)" />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <FooterButtons>
          <ResponsiveDesign.Desktop>
            <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft} aria-label="Back">
              Back
            </Button>
          </ResponsiveDesign.Desktop>
          <Button
            size="xl"
            onPress={handleSubmit}
            endIcon={ArrowRight}
            aria-label="Continue"
            isDisabled={isLoadingBrandProfile || isLoading}
            fullWidth={isMobile}
          >
            Continue
          </Button>
        </FooterButtons>
      </div>
    </MultiStepStepLayout>
  )
}
